import React, { useEffect } from 'react';

import { Card, Grid, Stack, Typography, useTheme, Divider } from '@mui/material';
import { componentLogger } from '@ragu-components/logger';
import { OrdIconStandard } from '@ragu-components/ord-components';
import { getRealmId } from '@ragu-components/ord-providers';
import { canAccessWithPermissions } from '@ratto-components/ra-rbac';
import { Link, usePermissions, useTranslate } from 'react-admin';

import { useIngordDataProvider } from 'src/components/flowchart/hooks/useIngordDataProvider';
import { useDomainLicense } from 'src/hooks/useDomainLicense';
import { useVisitedResourcesStore } from 'src/hooks/useVisitedResources';

import type { SxProps, Theme } from '@mui/material';
import type { DomainLicense } from '@ragu-components/ord-types';

const resourcesIcons = {
  users: 'users',
  groups: 'groups',
  queues: 'queues',
  labels: 'labels',
  contacts: 'contacts',
} as const;

const resourceToLicenseField: Partial<Record<keyof typeof resourcesIcons, keyof DomainLicense>> = {
  users: 'users',
  queues: 'queues',
};

const ResourceCard = ({
  resource,
  order,
  canList,
  canCreate,
  licenseLimit,
}: {
  resource: keyof typeof resourcesIcons;
  order: number;
  canList: boolean;
  canCreate: boolean;
  licenseLimit: number | undefined;
}): React.ReactElement => {
  const [total, setTotal] = React.useState(0);

  const theme = useTheme();
  const translate = useTranslate();
  const dataProvider = useIngordDataProvider();

  useEffect(() => {
    if (!canList) {
      return;
    }

    dataProvider
      .getList(resource, {
        filter: {},
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
      })
      .then(({ total }) => {
        setTotal(total ?? 0);
      })
      .catch((error) => {
        componentLogger.error(`Error fetching ${resource} count`, error);
      });
  }, [canList, dataProvider, resource]);

  return (
    <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={resource} sx={{ order }}>
      <HomeCard sx={{ height: '100%', p: theme.spacing(1.5, 3) }}>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Stack direction='row' alignItems='center' gap={1.5}>
            {resource in resourcesIcons ? <OrdIconStandard name={resourcesIcons[resource]} /> : null}
            <Typography variant='h6'>{translate(`resources.${resource}.name`, { _: resource })}</Typography>
          </Stack>
          <Typography variant='h4'>
            {total}
            {licenseLimit !== undefined && `/${licenseLimit}`}
          </Typography>
        </Stack>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          {translate(`ord.home.resourceDescription`, { resource })}
        </Typography>
        <Divider sx={{ mt: theme.spacing(3.5), mb: theme.spacing(1) }} />
        <Stack direction='row' gap={3}>
          {canList && <Link to={`/${resource}`}>{translate(`resources.${resource}.operations.edit`)}</Link>}
          {canCreate && <Link to={`/${resource}/create`}>{translate(`resources.${resource}.operations.create`)}</Link>}
        </Stack>
      </HomeCard>
    </Grid>
  );
};

export const Header = (): React.ReactElement => {
  const theme = useTheme();
  const translate = useTranslate();
  return (
    <Card
      sx={{
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Stack
        direction='row'
        justifyContent='space-between'
        padding={theme.spacing(3)}
        sx={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/bg-hompage.png)`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: '100% auto',
          minHeight: 270,
        }}
      >
        <Stack>
          <Typography gutterBottom variant='h6'>
            {translate('ord.home.title')}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {translate('ord.home.description')}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );
};

export const HomeCard = ({ children, sx }: React.PropsWithChildren<{ sx?: SxProps<Theme> }>): React.ReactElement => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        backgroundColor: theme.palette.background.paper,
        ...sx,
      }}
    >
      {children}
    </Card>
  );
};

const getLicenseLimit = (
  license: DomainLicense | undefined,
  resource: keyof typeof resourcesIcons
): number | undefined => {
  const licenseField = resourceToLicenseField[resource];
  if (licenseField === undefined || license === undefined) {
    return undefined;
  }
  const value = license[licenseField];
  return typeof value === 'number' ? value : undefined;
};

export const HomePage = (): React.ReactElement => {
  const theme = useTheme();
  const [visitedResources] = useVisitedResourcesStore();
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const { license } = useDomainLicense(getRealmId());

  const visibleResources = Object.keys(resourcesIcons).flatMap((resource) => {
    const canList = canAccessWithPermissions({ permissions, action: 'list', resource });
    const canCreate = canAccessWithPermissions({ permissions, action: 'create', resource });

    if (canList || canCreate) {
      return [{ resource, canList, canCreate }];
    }
    return [];
  });

  const visibleCount = visibleResources.length;

  // resourcecard have 10, 20, 30... positions, so visitedResources should have 5, 15, 25...
  const getPosition = (cols: number): number => {
    const total = visibleCount + 1;
    if (total <= cols) {
      // 5 -> first position (0 is the HomeCard)
      return 5;
    }
    // position of the first place of the last row
    const position = Math.floor(visibleCount / cols) * cols;
    return position * 10 + 5;
  };

  const smPosition = getPosition(2);
  const lgPosition = getPosition(3);

  return (
    <Grid
      container
      sx={{
        maxHeight: 'calc(100vh - 50px)',
        paddingBottom: 1,
        paddingTop: 1,
        [theme.breakpoints.down('md')]: { overflow: 'auto' },
      }}
      spacing={4}
    >
      <Grid size={12}>
        <Header />
      </Grid>
      {Object.entries(visitedResources).length > 0 && (
        <Grid
          size={{ xs: 12, sm: 6, lg: 4 }}
          data-testid='home-most-visited'
          sx={{
            order: 5,
            [theme.breakpoints.up('sm')]: { order: smPosition },
            [theme.breakpoints.up('lg')]: { order: lgPosition },
          }}
        >
          <HomeCard sx={{ height: '100%', p: theme.spacing(1.5, 3) }}>
            <Stack direction='row' alignItems='flex-start' gap={1.5}>
              <OrdIconStandard name='history' />
              <Stack>
                <Typography gutterBottom variant='h6'>
                  {translate('ord.home.mostVisited')}
                </Typography>
                <Stack sx={{ [theme.breakpoints.down('lg')]: { flexDirection: 'row', flexWrap: 'wrap' } }}>
                  {Object.entries(visitedResources)
                    .sort(([_resourceA, countA], [_resourceB, countB]) => countB - countA)
                    .slice(0, 4)
                    .map(([resource]) => (
                      <Link
                        key={resource}
                        to={`/${resource}`}
                        sx={{ [theme.breakpoints.down('lg')]: { width: '50%' } }}
                      >
                        {translate(`resources.${resource}.name`, { _: resource })}
                      </Link>
                    ))}
                </Stack>
              </Stack>
            </Stack>
          </HomeCard>
        </Grid>
      )}
      {visibleResources.map(({ resource, canList, canCreate }, index) => (
        <ResourceCard
          resource={resource as keyof typeof resourcesIcons}
          canList={canList}
          canCreate={canCreate}
          licenseLimit={getLicenseLimit(license, resource as keyof typeof resourcesIcons)}
          key={resource}
          order={(index + 1) * 10}
        />
      ))}
    </Grid>
  );
};
